import React, {useState, useEffect, useContext, useRef} from 'react';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import Loader from '../LoadingIndicator';
import _ from 'lodash';
import env from '../../../util/env';
import {serviceURL, UserLookupType, UserType} from '../../../util/uiconstants';
import {labels} from '../../../util/utils';
import styled from 'styled-components';
import {IconoirXmark, IconoirWarningCircle} from '@ey-xd/motif-icon';
import ReactSelect from '../SelectDropdown/SelectDropdown';
import Ellipse from '@ey/canvascoreservices/Ellipse';
import {getUserSearch} from '../../../actions/UserSearch/usersearchactions';
import {useDispatch, useSelector} from 'react-redux';
import OutputText from '@ey/canvascoreservices/OutputText';
/*
documentation:
This component can be used for searching internal or external users.
Once user types a name of a user and hits enter, he is presented with list of users.
On seleting user, caller component received the date via 
onUserSelectionChange.
*/

function UserSearch(props) {
	const projectId = props.projectId;
	const includeEngDetails = !!props?.includeEngDetails;
	const [selectedUser, setSelectedUser] = useState('');
	const [userOptions, setUserOptions] = useState([]);
	const [isUserOptionsOpen, setIsUserOptionsOpen] = useState(false);
	const [defaultValue, setDefaultValue] = useState('');
	const [searchDisabled, setSearchDisabled] = useState(props.isDisabled || false);
	const [isLoading, setIsLoading] = useState(false);
	const [startShowingResults, setStartShowingResults] = useState(false);
	const [callFailed, setCallFailed] = useState(false);
	const [failureError, setFailureError] = useState('');
	const [showValidations, setShowValidations] = useState(false);
	const [ignoreFirst, setIgnoreFirst] = useState(true);
	const [isMember, setIsMember] = useState(false);
	const [isGuest, setIsGuest] = useState(false);
	const [isClearClicked, setisClearClicked] = useState(false);

	const searchElement = useRef(null);
	const dispatch = useDispatch();
	const userSearchResults = useSelector((store) => store.userSearchResults);

	const showIsRequired = typeof props.showIsRequired !== 'undefined' ? props.showIsRequired : true;

	useEffect(() => {
		if (selectedUser === '' || props.canClearSelectedValue) {
			if (startShowingResults) {
				setIsUserOptionsOpen(true);
				if (userSearchResults) {
					let users = _.map(
						userSearchResults.users?.filter((user) => user.employmentStatus !== 'T'),
						props.getSingleUserOption || getSingleUserOption
					);
					setUserOptions(_.orderBy(users, (user) => user.label));
					setisClearClicked(false);
				} else setUserOptions([]);
			}
		}
	}, [userSearchResults]);

	useEffect(() => {
		if (selectedUser !== '') {
			selectedUser.allProps.isInternal || selectedUser.allProps.userType === UserType.Member
				? setIsMember(true)
				: setIsGuest(true);
		}
	}, [selectedUser]);

	useEffect(() => {
		if (!ignoreFirst) showValidationsForSearch();
		else setIgnoreFirst(false);
	}, [props.showValidations]);

	useEffect(() => {
		if (props.userError === true && (selectedUser === '' || props.user.isDuplicate || props.isDuplicateUser)) {
			setShowValidations(true);
		} else {
			setShowValidations(false);
		}
	}, [props.userError]);

	const showValidationsForSearch = () => {
		if (selectedUser && selectedUser !== 'default') setShowValidations(false);
		else setShowValidations(true);
	};

	const enterKeyPress = (e) => {
		setStartShowingResults(true);
		if (e.target.value === '') {
			setUserOptions([]);
		} else {
			if (props.lookupType === UserLookupType.External && !validateEmail(e.target.value)) {
				setShowValidations(true);
				return;
			}
			getUserSearchResults(e.target.value, props.lookupType);
		}
	};

	const formatOptionLabel = ({value, label}) => (
		<section>
			<section style={{'font-weight': 'bold'}}>{label}</section>
			<section style={{'font-size': 'small'}}>{value}</section>
		</section>
	);

	const formatGroupLabel = ({value}) => {
		<Ellipse content={value} noOfLines={1} />;
	};

	function validateEmail(emailField) {
		var reg = /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,63})$/;
		return reg.test(emailField) ? true : false;
	}

	const keyPress = (e) => {
		setShowValidations(false); //clear validations
		let isvalid = true;
		if (e.keyCode === 13 && !isUserOptionsOpen) {
			//enter press
			enterKeyPress(e);
		} else if (e.keyCode !== 38 && e.keyCode !== 40) {
			if (props.maxSearchLength && e.target.value && e.keyCode !== 8) {
				if (e.target.value.length >= props.maxSearchLength) {
					e.preventDefault();
				}
			}
			setIsUserOptionsOpen(false);
			setUserOptions([]);
		}
		setUserOptions([]);
	};

	const clearText = () => {
		setisClearClicked(true);
		if (props.onUserClear) {
			props.onUserClear(selectedUser, props.userListKey);
		}
		if (selectedUser !== '' || props.showErrorOnClear) {
			setShowValidations(true);
		}
		setSearchDisabled(false);
		setCallFailed(false);
		setFailureError('');
		setDefaultValue('');
		setSelectedUser('');
		setIsMember(false);
		setIsGuest(false);
		setIsUserOptionsOpen(false);
		if (props.id && !props.doNotDispatchUserEmpty && props.cleanUser) {
			props.cleanUser(props.id, true);
		}
	};

	useEffect(() => {
		if (props.clearUserData) {
			if (props.onUserClear) {
				props.onUserClear(selectedUser, props.userListKey);
			}
			setSearchDisabled(false);
			setCallFailed(false);
			setFailureError('');
			setDefaultValue('');
			setSelectedUser('');
			setIsMember(false);
			setIsGuest(false);
			setIsUserOptionsOpen(false);
			if (props.id && !props.doNotDispatchUserEmpty && props.cleanUser) {
				props.cleanUser(props.id, true);
			}
		}
	}, [props.clearUserData]);

	const getSingleUserOption = (user) => {
		if (props.showNameInOptions) {
			return {
				label: user.email,
				value: user.displayName,
				allProps: user
			};
		} else {
			return {
				label: user.displayName,
				value: user.email,
				allProps: user
			};
		}
	};

	const getUserSearchResults = (searchKey, userLookUpType) => {
		setCallFailed(false);
		setFailureError('');
		{
			let requestsUrl =
				env.getBaseURL(serviceURL) + 'Users/GetAll?searchString=' + searchKey + '&userLookupType=' + userLookUpType;
			if (includeEngDetails) {
				requestsUrl = requestsUrl + '&ProjectId=' + projectId + '&IncludeEngagementDetails=' + includeEngDetails;
			}
			setIsLoading(true);
			//setSearchDisabled(true);
			dispatch(getUserSearch(requestsUrl))
				.then((result) => {
					setIsLoading(false);
					setSearchDisabled(false);
					setUserSearchFocus();
				})
				.catch((e) => {
					setFailureError('Failure:' + e);
					setCallFailed(true);
					setIsLoading(false);
					setSearchDisabled(false);
					setUserOptions([]);
				});
		}
	};

	useEffect(() => {
		if (props.findSelectedUser) {
			const thisUser = props.findSelectedUser();
			if (thisUser && !isClearClicked) {
				setSelectedUser({
					label: '',
					value: thisUser.emailAddress,
					allProps: {gui: thisUser.userId, isInternal: thisUser.isInternal}
				});
				setSearchDisabled(true);
				setDefaultValue(thisUser.email);
				setShowValidations(false);
			}
		}
	}, []);

	useEffect(() => {
		if (isClearClicked) {
			setSelectedUser('');
			setSearchDisabled(false);
			setDefaultValue('');
			setIsMember(false);
			setIsGuest(false);
			setShowValidations(true);
			if (props.id && !props.doNotDispatchUserEmpty && props.cleanUser) {
				props.cleanUser(props.id, true);
			}
		}
	}, [isClearClicked]);

	// To collapse the dropdown on outside
	useEffect(() => {
		// add when mounted
		document.addEventListener('mousedown', handleClick);
		// return function to be called when unmounted
		return () => {
			document.removeEventListener('mousedown', handleClick);
		};
	}, []);

	const dropdown = useRef();

	const handleClick = (e) => {
		if (e.target.parentElement.parentElement.parentElement.classList.contains('react-select__menu-list')) {
			// inside click
			return;
		}

		setIsUserOptionsOpen(false);
		setStartShowingResults(false);
		// outside click
	};

	const setUserSearchFocus = () => {
		let ddl = document.querySelector(`#usersearch_${props.id}`);
		ddl.querySelector('.react-select__input').children[0].focus();
	};

	const renderSearchInstructionText = () => {
		const textProps = {
			size: 'S'
		};
		const desc = labels.userSearchPlaceholder;
		return (
			<OutputText body2 title={desc} className="enter-description" {...textProps}>
				{desc}
			</OutputText>
		);
	};

	return (
		<SearchBoxWrapper className="userfield-row nameandemail">
			{callFailed ? <section className="error"> {failureError} </section> : null}
			<section className="user-selectbox" ref={searchElement}>
				<StyledSelectBox>
					<ReactSelect
						ref={dropdown}
						className={
							'select-group' +
							(searchDisabled ? ' inputDisabled' : '') +
							(isMember ? ' isMember' : '') +
							(isGuest ? ' isGuest' : '') +
							(isLoading ? 'disabled' : '')
						}
						formLabel={props.lookupType === UserLookupType.External ? labels.emailForExternal : labels.nameOrEmail}
						noOptionsMessage={() => labels.noResultsFound}
						defaultValue={defaultValue}
						options={userOptions}
						theme={document.getElementsByTagName('body')[0].getAttribute('motif-theme') === 'light' ? null : 'dark'}
						isSearchable
						searchInLabelOrValue
						id={`usersearch_${props.id}`}
						errorMessage={
							props.user?.isDuplicate || props.isDuplicateUser
								? props.duplicateUserMessage || labels.duplicateUser
								: labels.required
						}
						showErrorMessage={showIsRequired && showValidations && props.userError}
						onLabelClickSetFocus={props.onLabelClickSetFocus}
						onChange={(option) => {
							if (option === null) {
								setShowValidations(false);
								setDefaultValue('');
								setSearchDisabled(false);
							} else {
								let opt = {label: '', value: option.value, allProps: option.allProps};
								if (props.showNameOnSelect) {
									opt = {
										label: '',
										value: option.allProps.firstName + ' ' + option.allProps.lastName,
										allProps: option.allProps
									};
								}
								setIsUserOptionsOpen(false);
								setDefaultValue(opt.label);
								!props.canClearSelectedValue && setSearchDisabled(true);
								setUserOptions([]);
								setSelectedUser(opt);
								setShowValidations(false);
								if (props.onUserSelectionChange) {
									props.onUserSelectionChange(opt, props.userListKey);
								}
							}
						}}
						isDisabled={searchDisabled}
						value={selectedUser}
						onInputKeyDown={keyPress}
						menuIsOpen={isUserOptionsOpen}
						formatOptionLabel={props.formatOptionLabel || formatOptionLabel}
						/* {(opt) => {
							if (_.isFunction(props.formatOptionLabel)) { return props.formatOptionLabel.bind(opt.value, opt.label) }
							else { return formatOptionLabel.bind(opt.value, opt.label); }
						}
						} */
						formatGroupLabel={formatGroupLabel}
						onBlurHideMenu={true}
					/>
				</StyledSelectBox>
				{!props.hideClearIcon && (
					<span
						title={labels.clearAllValues}
						className={
							'pull-left clearall' +
							(searchDisabled || (selectedUser !== '' && !props.canClearSelectedValue) ? ' disabled' : '')
						}
						onClick={() => {
							clearText();
						}}
					>
						<MotifIcon src={IconoirXmark} size="20" />
					</span>
				)}
				{isLoading && (
					<section className="enterdetail-loading">
						<Loader show view="inlineView" styleName="nameemailloader" />
					</section>
				)}
			</section>
			<section className="enterdetail-description">
				{showIsRequired && showValidations && props.userError ? (
					<span className="required-error">
						<MotifIcon src={IconoirWarningCircle} size="20" />
						<OutputText body2>
							{props.user?.isDuplicate || props.isDuplicateUser
								? props.duplicateUserMessage || labels.duplicateUser
								: labels.required}
						</OutputText>
					</span>
				) : selectedUser === '' && !props.hideUserSearchPlaceholder ? (
					renderSearchInstructionText()
				) : (
					''
				)}
			</section>
		</SearchBoxWrapper>
	);
}

export default UserSearch;

const SearchBoxWrapper = styled.section`
	width: 100%;
	.error,
	.show {
		color: ${(props) => props.theme.color[`red600`]};
		display: none !important;
	}

	.clearall.disabled {
		pointer-events: none;
		color: ${(props) => props.theme.color[`grey300`]};
		svg {
			cursor: not-allowed;
			fill: ${(props) => props.theme.color[`grey300`]};
		}
	}

	.motif-progress-loader-container {
		background-color: transparent;
	}

	.user-selectbox {
		display: inline-block;
		width: 100%;
		position: relative;
		.clearall {
			position: absolute;
			right: 1.3rem;
			top: 50%;
			transform: translate(0, -50%);
			svg {
				vertical-align: middle;
			}
		}

		.react-select__indicator-separator,
		.react-select__indicator {
			display: none;
		}

		.react-select__clear-indicator {
			svg {
				display: none;
			}
		}
		.select-group {
			label {
				font-size: 1.3rem;
				font-weight: lighter;
			}
			&.floating-label {
				label {
					top: 0.5rem;
				}
			}
			&.select-disabled {
				&.select-error {
					border-bottom: solid 1px ${(props) => props.theme.color[`red600`]};
				}
			}
		}

		.user-selectbox .pull-left {
			float: inherit !important;
		}
	}
	.enterdetail-description {
		.required-error {
			display: flex;
			color: ${(props) => props.theme.color[`red600`]};
			gap: 0.5rem;

			.motif-icon {
				color: ${(props) => props.theme.color[`red600`]};
			}
		}
	}

	.enterdetail-loading {
		height: 5rem;
		position: absolute;
		width: 100%;
		top: 0;
		right: 0;

		&:after {
			content: '';
			top: 0;
			right: 0;
			position: absolute;
			width: 100%;
			height: 100%;
			background: ${(props) => props.theme.color[`white`]};
			opacity: 0.5;
			z-index: 1;
		}
		.nameemailloader {
			padding-top: 1.6rem;
			z-index: 2;
		}
	}

	.enter-description {
		color: ${(props) => props.theme.color[`grey400`]};
	}
`;

const StyledSelectBox = styled.section`
	width: 100% !important;
	font-size: 1.4rem;
	color: ${(props) => props.theme.color[`grey400`]};
	label {
		font-size: 1.4rem;
	}
	.react-select-container {
		.react-select__control {
			min-height: 5rem;
			background: ${(props) => props.theme.color[`grey100grey450`]} !important;
		}
	}
	.react-select__input {
		width: 27rem;
	}
	&.inputDisabled > *,
	&.inputDisabled .react-select__single-value {
		color: ${(props) => props.theme.color[`grey400`]} !important;
	}

	&.disabled {
		cursor: not-allowed;
	}

	.isMember {
		border-left: 0.5rem solid ${(props) => props.theme.color[`blue400`]};
	}

	.isGuest {
		border-left: 0.5rem solid ${(props) => props.theme.color[`orange400`]};
	}

	.floating-label.inputDisabled.hideInput label {
		color: ${(props) => props.theme.color[`grey500`]} !important;
	}
`;
